/**
 * The book of running cycles — the whole of it, wherever it is read.
 *
 * There were two. This one grew on Обзор with the lenses, the attention
 * ordering, the sticky sortable heads, and a row that opens to show the exit
 * target, the trailing prices, the ladder and where the instrument stood when
 * it was taken. The other lived on Циклы — the screen named after the subject —
 * and was a flat eight-column table with no expansion at all. So the summary
 * screen told a reader more about a cycle than the screen built for cycles,
 * and the only way to study one was to leave Циклы and scroll to the bottom of
 * Обзор.
 *
 * That is what happens when one thing has two representations and no rule says
 * otherwise: they do not stay level, and it was the profile screen that fell
 * behind. There is one book now, and the two screens differ only in the two
 * ways they actually differ.
 *
 * **Capped or not.** On Обзор the book is the last of ten regions and must not
 * decide the length of the page, so it scrolls inside itself. On Циклы it is
 * the subject and runs to its full height.
 *
 * **What it is called.** Everything else — which cycles, in what order,
 * described how — is one question with one answer.
 */
import { Fragment, useState } from "react";
import { Button } from "@/design-system/Button";

import { AsOf } from "@/design-system/AsOf";
import { Panel } from "@/design-system/Panel";
import { Empty } from "@/design-system/states/States";
import type { SlotResponse } from "@/shared/api";
import { compareDecimal, isNegative, isZero, toPlaces, type Money } from "@/shared/money/money";

import { generationOfVersion, versionOf } from "@/shared/strategy/versions";

import { CYCLE_COLUMNS, CycleRow, type CycleColumn, type CycleSort, type SortKey } from "./CycleRow";

const VISIBLE = 8;
const DROP = 20;
const HALTED = new Set(["PAUSED", "EMERGENCY_STOPPED"]);

function fall(slot: SlotResponse): number | null {
  const entry = slot.skryptos_score_at_entry;
  const current = slot.current_skryptos_score;
  if (entry === null || entry === undefined || current === null || current === undefined) return null;
  return entry - current;
}

function attention(slot: SlotResponse): number {
  if (slot.blocked_reason !== null || HALTED.has(slot.state)) return 3;
  if (isNegative(toPlaces(slot.unrealised))) return 2;
  const drifted = fall(slot);
  return drifted !== null && drifted >= DROP ? 1 : 0;
}

/**
 * One ordering per sortable column.
 *
 * Money is compared as exact decimals, never as parsed numbers: the column a
 * reader sorts by is the column they are about to act on, and an ordering that
 * rounded first would put the wrong cycle at the top.
 */
function compare(key: SortKey, left: SlotResponse, right: SlotResponse): number {
  if (key === "pnl") return compareDecimal(right.unrealised, left.unrealised);
  if (key === "margin") return compareDecimal(right.margin, left.margin);
  // Best first, and a row with nothing measured sorts last rather than as
  // zero: unmeasured is not "flat".
  if (key === "return") {
    return compareDecimal(right.net_return ?? "-999", left.net_return ?? "-999");
  }
  if (key === "score") {
    return (right.current_skryptos_score ?? -1) - (left.current_skryptos_score ?? -1);
  }
  if (key === "age") {
    return (
      Date.parse(left.opened_at ?? new Date().toISOString()) -
      Date.parse(right.opened_at ?? new Date().toISOString())
    );
  }
  return 0;
}

function byAttention(left: SlotResponse, right: SlotResponse): number {
  const ranked = attention(right) - attention(left);
  if (ranked !== 0) return ranked;
  const result = compareDecimal(left.unrealised, right.unrealised);
  return result !== 0 ? result : left.slot_id.localeCompare(right.slot_id);
}

type Lens = "all" | "PRIME" | "PULSE" | "FLASH" | "legacy" | "losing" | "winning" | "unscored" | "ageing";

/** The pools a lens can filter by; shown only while a cycle trades in it. */
const POOL_LENSES = new Set<Lens>(["PRIME", "PULSE", "FLASH"]);

/** How long a cycle runs before it is worth looking at separately. */
const AGEING_HOURS = 48;

const LENS: readonly { readonly id: Lens; readonly label: string }[] = [
  { id: "all", label: "Все" },
  // The pools first: which pool a cycle trades in is the first question
  // about the book. The retired BASE v1.3 only while one of its cycles is open.
  { id: "PRIME", label: "PRIME" },
  { id: "PULSE", label: "PULSE" },
  { id: "FLASH", label: "FLASH" },
  { id: "legacy", label: "BASE v1.3" },
  { id: "losing", label: "В минусе" },
  { id: "winning", label: "В плюсе" },
  { id: "unscored", label: "Без оценки" },
  { id: "ageing", label: `Старше ${String(AGEING_HOURS)}ч` },
];

function holds(lens: Lens, slot: SlotResponse): boolean {
  if (lens === "all") return true;
  if (POOL_LENSES.has(lens)) return slot.logical_pool === lens;
  if (lens === "legacy") {
    // Read from the cycle, never from its slot label.
    const version = versionOf(slot);
    return version !== null && generationOfVersion(version) === "v1";
  }
  if (lens === "ageing") {
    if (slot.opened_at === null) return false;
    const hours = (Date.now() - new Date(slot.opened_at).getTime()) / 3_600_000;
    return hours >= AGEING_HOURS;
  }
  if (lens === "unscored") {
    return slot.current_skryptos_score === null || slot.current_skryptos_score === undefined;
  }
  const shown = toPlaces(slot.unrealised);
  if (isZero(shown)) return false;
  return lens === "losing" ? isNegative(shown) : !isNegative(shown);
}

export function CycleBook({
  slots,
  capitalLimit = null,
  asOf,
  ageSeconds,
  stale = false,
  title,
  region,
  capped = false,
}: {
  readonly slots: readonly SlotResponse[];
  readonly capitalLimit?: Money | null;
  readonly asOf?: string;
  readonly ageSeconds?: number;
  readonly stale?: boolean;
  /** What the panel is called where it stands. */
  readonly title: string;
  /**
   * The fixed-order region this book occupies, when it occupies one.
   *
   * Обзор marks its regions so the contract's order can be checked; Циклы is
   * one screen about one thing and has none. Absent rather than invented: a
   * region name on a screen with no fixed order would be a claim about a
   * contract that does not govern it.
   */
  readonly region?: string;
  /**
   * Whether the book scrolls inside itself rather than running to full height.
   *
   * True where it is one region among ten and must not set the length of the
   * page. False where it is the page.
   */
  readonly capped?: boolean;
}): React.JSX.Element {
  const [lens, setLens] = useState<Lens>("all");
  // Score and rank come from the ranking window; while no cycle in the book
  // has one, the columns and their lens are empty for every row and are not drawn.
  const scored = slots.some((slot) => slot.current_skryptos_score != null);
  // No sort chosen means the attention order: halted first, then losing, then
  // by result. A reader who has not asked for an order gets the one that
  // answers "what needs me".
  const [sort, setSort] = useState<CycleSort>({ key: null, descending: true });
  const filtered = [...slots].filter((slot) => holds(lens, slot));
  const chosen = sort.key;
  const shown =
    chosen === null
      ? filtered.sort(byAttention)
      : filtered.sort((left, right) => {
          const result = compare(chosen, left, right);
          return sort.descending ? result : -result;
        });

  if (slots.length === 0) {
    return (
      <div data-testid="slots" data-overview-region={region}>
        <Empty title="Активных циклов пока нет" detail="Новый цикл появится здесь после входа стратегии." />
      </div>
    );
  }

  return (
    <div data-overview-region={region}>
      <Panel
        title={title}
        labelledBy="slots-heading"
        testId="slots"
        flush
        trailing={
          asOf === undefined || !stale ? undefined : (
            <AsOf at={asOf} ageSeconds={ageSeconds ?? 0} stale={stale} testId="slots-as-of" />
          )
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border-subtle px-3 py-2 lg:px-4">
          <span className="text-helper-sm text-text-muted" data-testid="slots-order">
            Сначала требующие внимания · суммы в USDT
          </span>
          <div role="group" aria-label="Показать циклы" className="flex flex-wrap gap-0.5 rounded-row bg-background-base p-0.5">
            {LENS.filter((option) => (option.id !== "unscored" || scored) && ((option.id !== "legacy" && !POOL_LENSES.has(option.id)) || slots.some((slot) => holds(option.id, slot)))).map((option) => {
              const size = slots.filter((slot) => holds(option.id, slot)).length;
              const active = option.id === lens;
              return (
                <Button
                  key={option.id}
                  variant="ghost"
                  size="sm"
                  selected={active}
                  aria-pressed={active}
                  disabled={size === 0 && !active}
                  data-testid={`lens-${option.id}`}
                  onClick={() => { setLens(option.id); }}
                  className="gap-1 whitespace-nowrap pointer-coarse:min-h-11 pointer-coarse:px-3"
                >
                  {option.label}
                  <span className="tabular-nums text-text-muted">{size}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <div
          data-testid="cycle-scroll"
          className={capped && shown.length > VISIBLE ? "max-h-[44rem] overflow-y-auto" : ""}
        >
          {/* Nothing scrolls sideways (owner, 2026-10-15): paired figures fold
              into one column, and below lg every row is a card. */}
          <div>
            <table className="w-full border-collapse max-lg:block">
              {/* Sticky, so the heads stay over their columns while a book of
                  forty-five rows is read. */}
              <thead className="sticky top-0 z-10 bg-surface-default max-lg:hidden">
                <tr className="border-b border-border-strong">
                  <th className="w-6 bg-surface-default" />
                  {CYCLE_COLUMNS.filter((column) => scored || column.key !== "score").map((column) => (
                    <Fragment key={column.key}>
                      <Head column={column} sort={sort} onSort={setSort} folded={column.wide !== undefined} />
                      {column.wide?.map((part) => (
                        <Head key={part.key} column={part} sort={sort} onSort={setSort} wide />
                      ))}
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="max-lg:block">
                {shown.map((slot) => (
                  <CycleRow key={slot.slot_id} slot={slot} capitalLimit={capitalLimit} scored={scored} />
                ))}
              </tbody>
            </table>
          </div>
          {shown.length === 0 && (
            <p className="px-4 py-6 text-body-main text-text-muted" data-testid="lens-empty">
              Под этот фильтр сейчас не попадает ни один цикл.
            </p>
          )}
        </div>
      </Panel>
    </div>
  );
}

/**
 * One column head, sortable where the column says so.
 *
 * A paired column is drawn twice: folded (lg to xl) and split into its parts
 * (xl and wider), with the other copy hidden. The split heads carry their own
 * test ids, so a test that clicks a head finds exactly one.
 */
function Head({
  column,
  sort,
  onSort,
  folded = false,
  wide = false,
}: {
  readonly column: CycleColumn;
  readonly sort: CycleSort;
  readonly onSort: (update: (was: CycleSort) => CycleSort) => void;
  readonly folded?: boolean;
  readonly wide?: boolean;
}): React.JSX.Element {
  const visibility = wide ? "hidden xl:table-cell" : folded ? "xl:hidden" : "";
  const prefix = wide ? "sort-wide-" : "sort-";
  const button = (key: SortKey, label: string): React.JSX.Element => (
    <Button
      variant="ghost"
      size="sm"
      data-testid={`${prefix}${key}`}
      onClick={() => {
        onSort((was) =>
          was.key === key ? { key, descending: !was.descending } : { key, descending: true },
        );
      }}
      className="px-1 text-label-caps"
    >
      {label}
      {sort.key === key && <span aria-hidden="true">{sort.descending ? " ↓" : " ↑"}</span>}
    </Button>
  );
  return (
    <th
      scope="col"
      aria-sort={
        sort.key === column.key
          ? sort.descending
            ? "descending"
            : "ascending"
          : column.sortable
            ? "none"
            : undefined
      }
      className={`h-8 bg-surface-default px-3 text-label-caps whitespace-nowrap text-text-muted ${visibility} ${
        column.align === "right" ? "text-right" : "text-left"
      } ${column.key === "symbol" ? "sticky left-0 z-20" : ""}`}
    >
      {column.sortable === true ? (
        <span className={`inline-flex items-center gap-1 ${column.align === "right" ? "justify-end" : ""}`}>
          {button(column.key, column.label)}
          {column.also !== undefined && button(column.also.key, column.also.label)}
        </span>
      ) : (
        column.label
      )}
    </th>
  );
}
